import { format, isSameDay, isSameWeek, subDays } from 'date-fns'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'
import { useModal } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'
import { useArabicSessionLanguage, useSessionLanguage } from '@context/SessionSettingsContext'

import { formatDate, withTimeFormat } from '@utils/date.utils'
import { TransactionContext, getTransactionDisplay } from '@utils/transaction.utils'

import { TransactionDto } from '@model/TransactionDto'
import { WalletTypeEnum } from '@model/WalletDto'

import { dateFnsLocales } from '@global/locale/Locale'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { TransactionHistoryDrawerModal } from '@shared/components/TransactionHistoryDrawer'
import {
  DEPOSIT_TRANSACTION_TYPES,
  WITHDRAWAL_TRANSACTION_TYPES,
} from '@shared/components/TransactionTypeIcon/TransactionTypeIcon'

import {
  RecentActivityView,
  WalletActionKind,
  WalletRecentActivityGroupView,
} from '../wallets.types'
import { WalletsRoom, useRecentWalletTransactions } from './useRecentWalletTransactions'

interface UseWalletRecentActivityProps {
  room: WalletsRoom
  walletType: WalletTypeEnum
  walletId?: string
  context: TransactionContext
  historyPath: string
}

const getActivityKind = (transaction: TransactionDto, amountSign: '+' | '-'): WalletActionKind => {
  if (DEPOSIT_TRANSACTION_TYPES.includes(transaction.type.id) && amountSign === '+') {
    return 'deposit'
  }

  if (WITHDRAWAL_TRANSACTION_TYPES.includes(transaction.type.id) && amountSign === '-') {
    return 'withdraw'
  }

  return 'transfer'
}

export const useWalletRecentActivity = ({
  room,
  walletType,
  walletId,
  context,
  historyPath,
}: UseWalletRecentActivityProps): RecentActivityView => {
  const { t } = useTranslation()
  const locale = useSessionLanguage()
  const isArabic = useArabicSessionLanguage()
  const [auth] = useContext(AuthSessionContext)
  const { account } = useAccountReadContext()
  const { isDefaultCFDProductType } = useProductReadContext()
  const { formatMoney } = useFormatNumber()

  const { transactions, isLoading, isError } = useRecentWalletTransactions({
    room,
    walletType,
    walletId,
  })

  const getDrawerProps = useCallback(
    () => ({
      context,
      transactions,
      walletId,
    }),
    [context, transactions, walletId]
  )
  const drawerModal = useModal(TransactionHistoryDrawerModal, getDrawerProps)
  const { isOpen: isDrawerOpen, open: openDrawer, reRender: reRenderDrawer } = drawerModal

  useEffect(() => {
    if (isDrawerOpen) {
      reRenderDrawer()
    }
  }, [context, isDrawerOpen, reRenderDrawer, transactions, walletId])

  const items = useMemo<RecentActivityView['items']>(() => {
    const now = new Date()
    const yesterday = subDays(now, 1)
    const dateLocale = dateFnsLocales[locale]

    const getGroup = (date: Date): WalletRecentActivityGroupView => {
      if (isSameDay(date, now)) {
        return { id: 'today', label: t('Today') }
      }

      if (isSameDay(date, yesterday)) {
        return { id: 'yesterday', label: t('Yesterday') }
      }

      if (isSameWeek(date, now, { locale: dateLocale })) {
        return { id: 'this-week', label: t('This Week') }
      }

      return {
        id: format(date, 'yyyy-MM'),
        label: format(date, 'LLLL yyyy', { locale: dateLocale }),
      }
    }

    return transactions.map((transaction) => {
      const { fields, amountSign } = getTransactionDisplay(transaction, context, {
        isArabic,
        authId: account?.id,
        isDefaultCFDProductType: isDefaultCFDProductType(),
        walletId,
      })
      const kind = getActivityKind(transaction, amountSign)

      return {
        id: transaction.id,
        group: getGroup(new Date(transaction.createdDate)),
        kind,
        title: transaction.type.name,
        description: formatDate(transaction.createdDate, {
          formatType: withTimeFormat(auth?.dateFormatType?.name),
        }),
        amount: `${amountSign}${formatMoney(fields.amount, fields.currency)}`,
        onSelect: () => openDrawer({ initialTransactionId: transaction.id }),
      }
    })
  }, [
    account?.id,
    auth?.dateFormatType?.name,
    context,
    formatMoney,
    isArabic,
    isDefaultCFDProductType,
    locale,
    openDrawer,
    transactions,
    t,
    walletId,
  ])

  return {
    title: t('Recent Activity'),
    viewAllLabel: t('View All'),
    emptyTitle: t('No transactions yet'),
    emptyDescription: t('Wallet.No transactions description'),
    errorMessage: isError ? t('errors.Something went wrong') : undefined,
    isLoading,
    items,
    historyPath,
    walletId,
  }
}
